<template>
  <SCASelect
    :class="$style.select"
    :label
    :items="items"
    :model-value="selectedId"
    :disabled="disabled"
    :test-id="testId"
    @update:model-value="onChange"
  >
    <template #input>
      <SCADatetimeInputInline
        ref="editor"
        :model-value="editorValue"
        format="HH:mm"
        :disabled="disabled"
        @update:model-value="onEditorChange"
        @focusout="onEditorFocusOut"
      />
    </template>
  </SCASelect>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from "vue";

import SCASelect from "../SCASelect.vue";
import type { SelectableItem } from "../types";
import type { TimeParts } from "./datetimePickerTypes";
import SCADatetimeInputInline from "./SCADatetimeInputInline.vue";

const props = withDefaults(
  defineProps<{
    modelValue: TimeParts | null;
    minuteStep?: number;
    disabled?: boolean;
    label?: string;
    width?: number;
    testId?: string;
  }>(),
  {
    minuteStep: 15,
    disabled: false,
    label: undefined,
    width: 100,
    testId: undefined,
  },
);

const emit = defineEmits<{
  "update:modelValue": [value: TimeParts];
}>();

const editor = ref<InstanceType<typeof SCADatetimeInputInline>>();

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/** Options are every `minuteStep` minutes across the day; id = minutes since midnight. */
const items = computed<SelectableItem[]>(() => {
  const out: SelectableItem[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += props.minuteStep) {
      out.push({ id: h * 60 + m, label: `${pad(h)}:${pad(m)}` });
    }
  }
  // Keep the current value selectable even when it doesn't land on the step grid
  // (e.g. the 23:59 end-of-day time produced by the "all day" toggle).
  const current = props.modelValue;
  if (current && !out.some((i) => i.id === current.hours * 60 + current.minutes)) {
    out.push({ id: current.hours * 60 + current.minutes, label: `${pad(current.hours)}:${pad(current.minutes)}` });
    out.sort((a, b) => Number(a.id) - Number(b.id));
  }
  return out;
});

const selectedId = computed<number | undefined>(() => {
  if (!props.modelValue) return undefined;
  return props.modelValue.hours * 60 + props.modelValue.minutes;
});

const editorValue = computed<Date | null>(() => {
  if (!props.modelValue) return null;
  return new Date(2000, 0, 1, props.modelValue.hours, props.modelValue.minutes);
});

function onChange(id?: SelectableItem["id"]) {
  if (id === undefined) return;
  const total = Number(id);
  emit("update:modelValue", { hours: Math.floor(total / 60), minutes: total % 60, seconds: 0 });
}

function onEditorChange(date: Date | null) {
  if (!date) return;
  emit("update:modelValue", { hours: date.getHours(), minutes: date.getMinutes(), seconds: 0 });
}

function onEditorFocusOut(event: FocusEvent) {
  const root = event.currentTarget as HTMLElement;
  if (event.relatedTarget instanceof Node && root.contains(event.relatedTarget)) return;
  void nextTick(() => editor.value?.reset());
}
</script>

<style module lang="scss">
.select {
  width: v-bind("`${width}px`");
  --sca-label-size: 36px;
  --space-component-gap-medium: 4px;
}
</style>
